import { useState } from "react";
import { usePWAInstall } from "../lib/pwa";
import { API } from "../lib/api";

/**
 * Desktop setup card shown after a user saves a terminal. The desktop app has
 * two parts (the app window + the menu bar), but we present them as ONE simple
 * setup — minimal copy, no mechanics-explaining.
 */
export default function DesktopTerminalCard() {
  const { installed, promptInstall } = usePWAInstall();
  const [hint, setHint] = useState(false);
  const [steps, setSteps] = useState(false);

  const isChromium =
    typeof navigator !== "undefined" &&
    /Chrome|Chromium|Edg/.test(navigator.userAgent) &&
    !/Mobile|Android|iPhone|iPad/.test(navigator.userAgent);

  const onInstallApp = async () => {
    const ok = await promptInstall();
    if (!ok) setHint(true);
  };

  return (
    <div className="border-2 border-black p-4 mt-5 bg-[#FBF8EE]" data-testid="desktop-setup-card">
      <div className="font-bold mb-1">{">"}_ get the desktop app</div>
      <p className="text-[#555] leading-snug">Two parts — install both.</p>

      {/* Step 1 — the app */}
      <div className="mt-3 flex items-baseline gap-3" data-testid="setup-step-app">
        <span className="text-[#999] font-bold text-[12px]">1.</span>
        <div>
          {installed ? (
            <div className="font-bold text-[13px]" data-testid="setup-app-done">app installed ✓</div>
          ) : (
            <button
              type="button"
              onClick={onInstallApp}
              className="border border-black bg-black text-white px-3 py-2 text-[12px] font-bold inline-block hover:bg-[#222] transition-colors"
              data-testid="setup-install-app"
            >
              [ install the app → ]
            </button>
          )}
          {hint && !installed && (
            <p className="text-[11px] text-[#888] mt-1.5" data-testid="setup-app-hint">
              No one-click install? Use the install icon in your address bar — or on
              iPhone, <span className="font-bold">Share → Add to Home Screen</span>.
            </p>
          )}
        </div>
      </div>

      {/* Step 2 — the menu bar */}
      <div className="mt-3 flex items-baseline gap-3" data-testid="setup-step-bar">
        <span className="text-[#999] font-bold text-[12px]">2.</span>
        <div>
          <div className="flex flex-wrap gap-2">
            <a
              href={`${API}/extension/download`}
              className="border border-black bg-black text-white px-3 py-2 text-[12px] font-bold inline-block hover:bg-[#222] transition-colors no-underline"
              data-testid="setup-download-bar"
              onClick={() => setSteps(true)}
            >
              [ install the extension → ]
            </a>
            <button
              type="button"
              onClick={() => setSteps((s) => !s)}
              className="text-[12px] text-[#888] hover:text-black hover:underline"
              data-testid="setup-bar-steps-toggle"
            >
              {steps ? "hide" : "how?"}
            </button>
          </div>

          {!isChromium && (
            <p className="text-[11px] text-[#888] mt-1.5" data-testid="setup-bar-chrome-note">
              Use Chrome or Edge on desktop for this part.
            </p>
          )}

          {steps && (
            <ol
              className="text-[11px] text-[#444] mt-2 pl-4 list-decimal space-y-1 leading-snug"
              data-testid="setup-bar-steps"
            >
              <li>Unzip the download.</li>
              <li>
                Go to <span className="font-mono">chrome://extensions</span> and turn on{" "}
                <span className="font-bold">Developer mode</span>.
              </li>
              <li>Click <span className="font-bold">Load unpacked</span> and pick the folder.</li>
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
